/**
 * Erreurs JavaScript des visiteurs → journal des erreurs du site (POST /api/site-errors/client,
 * écran « Erreurs du site » de l'admin, outils MCP `site_errors_*`). Repris de moins-depenser
 * (17-18/10/2026).
 *
 * Sans donnée personnelle : la page sans sa requête, le fichier fautif, le message et la pile ;
 * ni contenu de formulaire. Cinq envois au plus par page chargée, une fois par message.
 *
 * Chaque erreur emporte son contexte : langues du navigateur, page traduite ou non, extensions
 * qui modifient la page, moment par rapport à l'hydratation. Une hydratation manquée (React
 * #418) emporte en plus le premier écart entre le texte du serveur et la page reconstruite, et
 * les noms des éléments étrangers à la page. Le navigateur et l'appareil sont lus par l'API.
 *
 * React 19 signale ses erreurs (non rattrapées, hydratation rattrapée) par `reportError`, qui
 * passe par l'évènement `error` de la fenêtre : un seul écouteur suffit.
 */

const MAX_PER_PAGE = 5
const ENDPOINT = '/api/site-errors/client'
const seen = new Set<string>()
let sent = 0

type BrowserErrorType = 'js' | 'rejection' | 'load'

interface BrowserError {
  type: BrowserErrorType
  message: string
  source: string
  line?: number
  column?: number
  stack?: string
}

type Win = Window & { __mfHydration?: string; __mfInitialPage?: { html: string; path?: string }; __mfBrowserErrors?: boolean }

function withoutQuery(value: unknown): string {
  return String(value || '').split(/[?#]/)[0].slice(0, 300)
}

/** Moment de l'erreur : avant l'hydratation, pendant ses 3 premières secondes, ou après. */
export function markHydration(state: 'pendant' | 'apres'): void {
  if (typeof window === 'undefined') return
  const w = window as Win
  w.__mfHydration = state
  if (state === 'pendant') setTimeout(() => { w.__mfHydration = 'apres' }, 3_000)
}

/** Garde le HTML du serveur tel que le navigateur l'a reçu, AVANT que React ne le touche. */
export function rememberInitialPage(root: HTMLElement | null): void {
  if (typeof window === 'undefined' || !root) return
  try { (window as Win).__mfInitialPage = { html: root.innerHTML, path: window.location.pathname } } catch { /* sans écart */ }
}

/** Extensions et traductions qui réécrivent la page avant ou pendant l'hydratation. */
export function domModificationHints(doc: Document = document) {
  const html = doc.documentElement
  const body = doc.body
  const has = (el: Element | null, name: string) => Boolean(el && el.hasAttribute && el.hasAttribute(name))
  const extensions: string[] = []
  if (has(body, 'data-gr-ext-installed') || has(body, 'data-new-gr-c-s-check-loaded') || doc.querySelector('grammarly-desktop-integration, grammarly-extension')) extensions.push('grammarly')
  if ([...(html?.attributes || [])].some((x) => x.name.startsWith('data-darkreader')) || doc.querySelector('meta[name="darkreader"], style.darkreader')) extensions.push('darkreader')
  if (has(body, 'cz-shortcut-listen')) extensions.push('colorzilla')
  if (doc.querySelector('[data-lastpass-root], [data-lastpass-icon-root]')) extensions.push('lastpass')
  if (doc.querySelector('[data-dashlanecreated], [data-dashlane-rid]')) extensions.push('dashlane')
  if (doc.querySelector('[data-bitwarden-watching]')) extensions.push('bitwarden')
  const classes = html?.className ? String(html.className) : ''
  const translationFont = Boolean(doc.querySelector('#root font[style]'))
  const googleTranslate = /translated-(ltr|rtl)/.test(classes) || translationFont
  const htmlLang = String(html?.getAttribute('lang') || '').slice(0, 20)
  // Le site parle sept langues : « traduit » = traduit par le NAVIGATEUR, pas une autre langue du site
  return { translated: googleTranslate, htmlLang, googleTranslate, extensions }
}

/**
 * Éléments que la page n'a pas posés : balises personnalisées (extension de coupons, de
 * traduction…) et enfants directs du <body> autres que les nôtres. Noms seuls, cinq au plus.
 */
export function foreignElements(doc: Document = document): string[] {
  const names = new Set<string>()
  for (const el of doc.querySelectorAll('*')) {
    if (names.size >= 5) break
    const tag = el.tagName.toLowerCase()
    if (tag.includes('-')) names.add(tag)
  }
  for (const el of Array.from(doc.body?.children || [])) {
    if (names.size >= 5) break
    const tag = el.tagName.toLowerCase()
    if (el.id === 'root' || ['script', 'noscript', 'style', 'link', 'template'].includes(tag)) continue
    names.add(`${tag}${el.id ? `#${el.id}` : ''}`.slice(0, 40))
  }
  return [...names]
}

/** Texte visible d'un HTML, sans marqueurs Suspense, scripts, styles ni icônes. */
export function visibleText(html: unknown, doc: Document = document): string | null {
  if (typeof html !== 'string') return null
  const block = doc.createElement('div')
  block.innerHTML = html
  block.querySelectorAll('script, style, svg, template').forEach((el) => el.remove())
  return String(block.textContent || '').replace(/\s+/g, ' ')
}

/**
 * Premier écart entre deux textes : celui du HTML du serveur au démarrage (extensions et
 * traduction comprises) et celui de la page que React vient de reconstruire. Le texte plutôt
 * que le HTML : les marqueurs Suspense et les classes posées après coup différaient toujours.
 */
export function firstDifference(before: unknown, after: unknown, margin = 120) {
  if (typeof before !== 'string' || typeof after !== 'string' || before === after) return null
  const n = Math.min(before.length, after.length)
  let i = 0
  while (i < n && before.charCodeAt(i) === after.charCodeAt(i)) i += 1
  const start = Math.max(0, i - 80)
  return { position: i, server: before.slice(start, i + margin), client: after.slice(start, i + margin) }
}

/** Pile d'une erreur et de sa cause (React #520 ne dit rien par lui-même, la cause si). */
export function withCause(error: unknown): string | undefined {
  if (!error) return undefined
  const e = error as { stack?: string; message?: string; cause?: unknown }
  const stack = String(e.stack || e.message || '')
  const cause = e.cause
  if (!cause) return stack || undefined
  const text = cause instanceof Error ? `${cause.name}: ${cause.message}\n${cause.stack || ''}` : String(cause)
  return `${stack}\nCause : ${text}`
}

const HYDRATION = /react\.dev\/errors\/(418|423|425)\b|Hydration failed|didn't match/i

function context(extra?: Record<string, unknown>) {
  try {
    return {
      language: String(navigator.language || '').slice(0, 20),
      languages: Array.from(navigator.languages || []).slice(0, 5).map((l) => String(l).slice(0, 20)),
      ...domModificationHints(),
      hydration: (window as Win).__mfHydration || 'avant',
      ...extra,
    }
  } catch {
    return undefined
  }
}

/** Corps envoyé à l'API — exporté pour les tests. */
export function buildReport(error: BrowserError, page: string, ctx: unknown) {
  return {
    errors: [{
      ...error,
      message: String(error.message || '').slice(0, 500),
      stack: error.stack ? String(error.stack).slice(0, 4000) : undefined,
      page,
      context: ctx,
    }],
  }
}

function send(error: BrowserError, extra?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || sent >= MAX_PER_PAGE) return
  const key = `${error.type}|${error.message}|${error.source}`
  if (seen.has(key)) return
  seen.add(key)
  sent += 1
  const body = JSON.stringify(buildReport(error, window.location.pathname, context(extra)))
  try {
    // text/plain : les vieux navigateurs refusent un beacon JSON (cf. ClientErrorController)
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) return
    void fetch(ENDPOINT, { method: 'POST', headers: { 'content-type': 'application/json' }, body, credentials: 'include' }).catch(() => {})
  } catch {
    // Le signalement ne doit jamais gêner la page
  }
}

export function installBrowserErrorCapture(): void {
  if (typeof window === 'undefined') return
  const w = window as Win
  if (w.__mfBrowserErrors) return
  w.__mfBrowserErrors = true

  window.addEventListener('error', (event: ErrorEvent | Event) => {
    const target = event.target as (HTMLScriptElement & HTMLLinkElement) | null
    // Un script ou une feuille de style qui ne charge pas (déploiement, coupure réseau)
    if (target && (target as unknown) !== window && (target.tagName === 'SCRIPT' || (target.tagName === 'LINK' && target.rel === 'stylesheet'))) {
      const address = withoutQuery(target.src || target.href)
      if (address.startsWith(window.location.origin)) {
        // Signalé seulement si le fichier manque vraiment : un réseau coupé ou une page quittée
        // pendant le chargement faisait remonter des fichiers présents
        fetch(address, { method: 'HEAD', cache: 'no-store', credentials: 'omit' })
          .then((res) => {
            if (!res.ok) send({ type: 'load', message: `Chargement impossible (${res.status}) : ${address.replace(window.location.origin, '')}`, source: address })
          })
          .catch(() => {})
      }
      return
    }
    const e = event as ErrorEvent
    if (!e.message) return
    const error: BrowserError = {
      type: 'js',
      message: e.message,
      source: withoutQuery(e.filename),
      line: Number.isInteger(e.lineno) ? e.lineno : undefined,
      column: Number.isInteger(e.colno) ? e.colno : undefined,
      stack: withCause(e.error),
    }
    if (!HYDRATION.test(e.message)) { send(error); return }
    // Hydratation manquée : on compare le texte de la page reconstruite à celui du démarrage
    let diff: ReturnType<typeof firstDifference> = null
    try { diff = firstDifference(visibleText(w.__mfInitialPage?.html), visibleText(document.getElementById('root')?.innerHTML)) } catch { /* sans écart */ }
    send(error, { foreign: foreignElements(), ...(diff ? { diff } : {}) })
  }, true)

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason
    const message = reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason ?? 'Promesse rejetée')
    // Requêtes annulées en quittant la page : sans intérêt
    if (/AbortError|CanceledError|canceled/i.test(message)) return
    send({ type: 'rejection', message, source: '', stack: reason?.stack })
  })
}
